import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Field, Icon, SelectField } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { ApiClientError, api, isDemoMode } from "../api/client";
import type { Task } from "@nelurio/contracts";
import { saveRoutine, type Routine } from "../lib/routines";
import { BUILTIN_KEY, BUILTIN_KINDS, BUILTIN_LABELS, SERVER_KINDS, addCategory, deleteCategory, readCategories, readOverrides, renameBuiltin, renameCategory, toggleBuiltinHidden, visibleBuiltins, type Category } from "../lib/categories";
import { normalizeDescription } from "../lib/taskDetails";

export interface TaskComposerProps {
  open: boolean;
  /** Default day (yyyy-mm-dd) the composer was opened for. */
  dateKey: string;
  /** When set, the composer edits this task instead of creating one. */
  task?: Task | null;
  /** When set, the composer edits this routine instead of creating one. */
  routine?: Routine | null;
  onClose: () => void;
  onSaved: (task: Task | null, routine?: Routine) => void;
}

/**
 * Full task creation/edit popup: name, category, time, optional conversion to
 * a repeating routine (weekday picker) and optional one-off special date.
 */
export function TaskComposer({ open, dateKey, task, routine: editRoutine, onClose, onSaved }: TaskComposerProps) {
  const { t, locale } = useI18n();
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<string>("");  // empty by default — the user picks the category
  const [withTime, setWithTime] = useState(false);
  const [time, setTime] = useState("10:00");
  const [date, setDate] = useState(dateKey);
  const [asRoutine, setAsRoutine] = useState(false);
  const [routineDays, setRoutineDays] = useState<number[]>([1, 3, 5]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [custom, setCustom] = useState<Category[]>(() => readCategories());
  const [manageOpen, setManageOpen] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [overrides, setOverrides] = useState(() => readOverrides());
  const [builtinEdit, setBuiltinEdit] = useState<string | null>(null);
  const [builtinName, setBuiltinName] = useState("");
  const [hasPriority, setHasPriority] = useState(false);
  const [priority, setPriority] = useState("1");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (editRoutine) {
      setTitle(editRoutine.title);
      setKind(editRoutine.kind);
      setWithTime(Boolean(editRoutine.time));
      setTime(editRoutine.time || "10:00");
      setDate(dateKey);
      setAsRoutine(true);
      setRoutineDays(editRoutine.days);
      setHasPriority(false);
      return;
    }
    if (task) {
      setTitle(task.title);
      setKind(task.kind);
      setWithTime(Boolean(task.dueTime));
      setTime(task.dueTime || "10:00");
      setDate(task.dueDate);
      setDescription(task.description || "");
      setAsRoutine(false);
      setHasPriority(task.priority === "high" || Number((task as Task & { priorityNum?: number }).priorityNum) > 0);
      setPriority(String((task as Task & { priorityNum?: number }).priorityNum ?? 1));
    } else {
      setTitle("");
      setDescription("");
      setKind("");
      setWithTime(false);
      setTime("10:00");
      setDate(dateKey);
      setAsRoutine(false);
      setRoutineDays([1, 3, 5]);
      setHasPriority(false);
      setPriority("1");
    }
  }, [open, task, editRoutine, dateKey]);

  if (!open) return null;

  const toggleDay = (day: number) =>
    setRoutineDays((current) => (current.includes(day) ? current.filter((value) => value !== day) : [...current, day].sort()));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return; // double-click / Enter spam must not create twice
    if (!title.trim()) { setError(t("task.titleRequired")); return; }
    if (!kind) { setError(t("task.categoryRequired")); return; }
    if (!date) { setError(t("task.dateRequired")); return; }
    if (asRoutine && !task && routineDays.length === 0) { setError(t("task.daysRequired")); return; }
    setSaving(true);
    setError("");
    try {
      if (asRoutine && !task) {
        const routine: Routine = { id: editRoutine?.id ?? "", title: title.trim(), kind, days: routineDays, time: withTime ? time : null };
        await saveRoutine(routine, !editRoutine);
        onSaved(null, routine);
      } else if (task) {
        // PATCH answers {"updated":true}, not the task — merge the applied patch
        // locally. dueTime "" explicitly clears the time (null means "no change").
        const patch = { title: title.trim(), description: normalizeDescription(description) ?? "", kind, dueDate: date, dueTime: withTime ? time : "", priority: hasPriority ? "high" : "normal", priorityNum: hasPriority ? Number(priority) || 1 : 0 };
        if (!isDemoMode()) await api(`/tasks/${task.id}`, { method: "PATCH", body: JSON.stringify(patch) });
        const updated: Task = { ...task, ...patch, dueTime: withTime ? time : null } as Task;
        onSaved(updated);
      } else {
        const payload = { title: title.trim(), description: normalizeDescription(description), kind, dueDate: date, dueTime: withTime ? time : null, priority: hasPriority ? "high" : "normal", priorityNum: hasPriority ? Number(priority) || 1 : 0 };
        const created = isDemoMode()
          ? { id: crypto.randomUUID(), status: "open", ...payload } as Task
          : await api<Task>("/tasks", { method: "POST", body: JSON.stringify(payload) });
        onSaved(created);
      }
      onClose();
    } catch (reason) {
      // Server validation errors carry a code — surface the SPECIFIC problem
      // (e.g. a custom category longer than 40 chars) instead of a generic
      // "something went wrong" that gave no hint what to fix.
      if (reason instanceof ApiClientError && reason.code === "invalid_kind") setError(t("task.kindInvalid"));
      else if (reason instanceof ApiClientError && reason.code === "invalid_date") setError(t("task.dateRequired"));
      else setError(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const weekdayLabel = (day: number) => new Intl.DateTimeFormat(locale, { weekday: "short" }).format(new Date(2024, 0, 7 + day));

  // Portal: an ancestor with transform/filter would otherwise turn the fixed
  // backdrop into a local container — the popup must always cover the page.
  return createPortal(
    <div className="composer-backdrop" onClick={onClose} role="presentation">
      <div className="composer-modal rise" role="dialog" aria-modal="true" aria-label={task ? t("task.edit") : editRoutine ? t("routine.editTitle") : t("task.new")} onClick={(event) => event.stopPropagation()}>
        <div className="composer-modal__head">
          <h2>{task ? t("task.edit") : editRoutine ? t("routine.editTitle") : t("task.new")}</h2>
          <button className="composer-modal__close" onClick={onClose} aria-label={t("common.cancel")}><Icon name="x" size={16} /></button>
        </div>
        <form onSubmit={submit}>
          <Field label={t("task.title")} value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t("task.titlePlaceholder")} required autoFocus />
          <label className="nl-field">
            <span className="nl-field__label">{t("task.description")}</span>
            <textarea
              className="nl-input nl-textarea"
              rows={2}
              maxLength={500}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t("task.descriptionPlaceholder")}
            />
          </label>
          <div className="composer-category">
            <SelectField label={t("task.category")} value={kind} onChange={(event) => setKind(event.target.value)}>
              <option value="" disabled hidden>{t("task.categoryPlaceholder")}</option>
              {visibleBuiltins().map((value) => <option key={value} value={value}>{overrides.renamed[value] ?? t(BUILTIN_KEY[value])}</option>)}
              {SERVER_KINDS.filter((value) => !BUILTIN_KINDS.includes(value as never)).map((value) => <option key={value} value={value}>{t(BUILTIN_LABELS[value] ?? value)}</option>)}
              {custom.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
              {/* Hidden built-ins are NOT listed — that is what hiding means.
                  Exception: the task being edited still carries that kind, so its
                  option stays to keep the controlled select valid. */}
              {overrides.hidden.filter((value) => kind === value).map((value) => <option key={value} value={value}>{overrides.renamed[value] ?? t(BUILTIN_KEY[value] ?? value)}</option>)}            </SelectField>
            <button type="button" className="composer-category__edit" onClick={() => setManageOpen((value) => !value)} aria-label={t("task.manageCategories")} title={t("task.manageCategories")}>
              <Icon name="edit" size={15} />
            </button>
          </div>
          {manageOpen && (
            <div className="composer-manage">
              <span className="nl-field__label">{t("task.manageCategories")}</span>
              <ul>
                {BUILTIN_KINDS.map((value) => (
                  <li key={value}>
                    {builtinEdit === value ? (
                      <>
                        <input value={builtinName} onChange={(event) => setBuiltinName(event.target.value)} autoFocus maxLength={40} />
                        <button type="button" onClick={() => { setOverrides(renameBuiltin(value, builtinName)); setBuiltinEdit(null); }} aria-label={t("common.save")}><Icon name="check" size={13} /></button>
                      </>
                    ) : (
                      <>
                        <strong>{overrides.renamed[value] ?? t(BUILTIN_KEY[value])}</strong>
                        <button type="button" onClick={() => { setBuiltinEdit(value); setBuiltinName(overrides.renamed[value] ?? ""); }} aria-label={t("common.edit")}><Icon name="edit" size={13} /></button>
                        <button type="button" onClick={() => setOverrides(toggleBuiltinHidden(value))} aria-label={overrides.hidden.includes(value) ? t("task.restoreCategory") : t("common.delete")}><Icon name={overrides.hidden.includes(value) ? "eye" : "trash"} size={13} /></button>
                      </>
                    )}
                  </li>
                ))}
                {custom.map((item) => (
                  <li key={item.id}>
                    {editingId === item.id ? (
                      <>
                        <input value={editingName} onChange={(event) => setEditingName(event.target.value)} autoFocus />
                        <button type="button" onClick={() => { const next = renameCategory(item.id, editingName || item.name); setCustom(next); setEditingId(null); }} aria-label={t("common.save")}><Icon name="check" size={13} /></button>
                      </>
                    ) : (
                      <>
                        <strong>{item.name}</strong>
                        <button type="button" onClick={() => { setEditingId(item.id); setEditingName(item.name); }} aria-label={t("common.edit")}><Icon name="edit" size={13} /></button>
                        <button type="button" onClick={() => { const next = deleteCategory(item.id); setCustom(next); if (kind === item.name) setKind(""); }} aria-label={t("common.delete")}><Icon name="trash" size={13} /></button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <div className="composer-manage__add">
                <input value={newCategory} onChange={(event) => setNewCategory(event.target.value)} placeholder={t("task.newCategory")} maxLength={40} />
                <button type="button" disabled={!newCategory.trim()} onClick={() => { const next = addCategory(newCategory); setCustom(next); setKind(newCategory.trim()); setNewCategory(""); }} aria-label={t("common.add")}><Icon name="plus" size={13} /></button>
              </div>
              <small>{t("task.manageHint")}</small>
            </div>
          )}
          <Field label={t("task.day")} type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          <label className="toggle-row">
            <input type="checkbox" checked={withTime} onChange={(event) => setWithTime(event.target.checked)} />
            <span>{t("task.optionalTime")}</span>
          </label>
          {withTime && <Field label={t("task.dueTime")} type="time" value={time} onChange={(event) => setTime(event.target.value)} />}

          {(!task || editRoutine) && (
            <>
              {!editRoutine && (
                <label className="toggle-row">
                  <input type="checkbox" checked={asRoutine} onChange={(event) => setAsRoutine(event.target.checked)} />
                  <span>{t("task.routineToggle")}</span>
                </label>
              )}
              {(asRoutine || editRoutine) && (
                <div className="composer-routine">
                  <span className="nl-field__label">{t("task.routineDays")}</span>
                  <div className="composer-weekdays" role="group" aria-label={t("task.routineDays")}>
                    {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                      <button
                        key={day}
                        type="button"
                        className={routineDays.includes(day) ? "is-active" : ""}
                        onClick={() => toggleDay(day)}
                        aria-pressed={routineDays.includes(day)}
                      >
                        {weekdayLabel(day)}
                      </button>
                    ))}
                  </div>
                  <small>{t("task.routineHint")}</small>
                </div>
              )}
            </>
          )}

          <label className="toggle-row">
            <input type="checkbox" checked={hasPriority} onChange={(event) => setHasPriority(event.target.checked)} />
            <span>{t("task.priorityToggle")}</span>
          </label>
          {hasPriority && (
            <div className="composer-priority">
              <span className="nl-field__label">{t("task.priorityLabel")}</span>
              <div className="composer-priority__row">
                <input type="number" min={1} max={9} value={priority} onChange={(event) => setPriority(event.target.value)} />
                <small>{Number(priority) === 1 ? t("task.priorityTopHint") : t("task.priorityHint")}</small>
              </div>
            </div>
          )}

          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="composer-modal__actions">
            <Button type="button" variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" loading={saving}>{t("common.save")}</Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
